/**
 * Версия продукта и журнал изменений.
 *
 * Один источник на всё приложение: и на подпись в меню, и на ответ health,
 * и на страницу журнала. Раньше версия была вписана строкой в трёх
 * package.json и ещё раз руками в ответе health — они уже разошлись бы при
 * первом же выпуске, потому что обновить надо было четыре места.
 *
 * Нумерация трёхчастная, и части значат разное:
 *
 *   0 . 0 . 1
 *   │   │   └── выпуск: любое изменение, дошедшее до боевого стенда
 *   │   └────── подверсия: заметная порция работы, как месяц внутри сезона
 *   └────────── стабильная версия: смена поколения продукта, как сезон
 *
 * Даты в номер не входят: номер говорит о том, насколько изменился продукт,
 * а не о том, когда это случилось. Когда — написано рядом в записи журнала.
 */
export const APP_VERSION = '0.1.1';

export interface DevlogEntry {
  /** Номер выпуска, к которому относится запись. */
  version: string;
  /** Дата выпуска, YYYY-MM-DD. Только для показа, в номер не входит. */
  date: string;
  /**
   * Что изменилось — человеческим языком.
   *
   * Журнал читают те, кто пользуется программой, а не те, кто её пишет.
   * Поэтому здесь «исправили вход по ссылке», а не «добавлена проверка
   * membership в buildIcs»: название функции ничего не говорит человеку,
   * которому важно, что ссылка снова работает.
   */
  changes: {
    ru: string[];
    en: string[];
  };
}

/**
 * Записи журнала, новые сверху.
 *
 * Журнал публичный. Правила, что сюда попадает и что нет, — в
 * `docs/AI_RULES.md`, раздел «Публичный DevLog». Коротко: пишем пользовательский
 * результат, а не то, как он получен; внутреннее устройство, инфраструктура,
 * резервные копии, механизмы безопасности и административная панель сюда не
 * попадают вовсе. Если пользовательских изменений в выпуске не было — один общий
 * пункт честнее шести придуманных.
 */
export const DEVLOG: readonly DevlogEntry[] = [
  {
    version: '0.1.1',
    date: '2026-09-09',
    changes: {
      ru: [
        'Аккаунтам без выбранного региона теперь сразу предлагается выбрать его — без этого доступ к остальным разделам EsportCRM ограничен',
        'Баланс EsportCRM теперь виден прямо в шапке приложения, отдельно от финансов организации',
        'Улучшили работу с тарифами и настройками биллинга',
      ],
      en: [
        'Accounts without a chosen region are now prompted to pick one right away — the rest of EsportCRM stays unavailable until then',
        "Your EsportCRM balance is now visible right in the app header, kept separate from your organization's finances",
        'Improved how plans and billing settings work',
      ],
    },
  },
  {
    version: '0.1.0',
    date: '2026-09-09',
    changes: {
      ru: [
        'Добавили платёжный аккаунт EsportCRM — баланс и история операций',
        'Регион аккаунта теперь определяет валюту расчётов: рубли или доллары',
        'Курс для операций в разных валютах берётся из официального источника Центробанка',
        'Добавили смену региона с пересчётом остатка в новую валюту по курсу на дату операции',
        'Корректировки баланса поддержкой отображаются в истории с указанием причины',
        'Повысили точность и надёжность финансовых операций и внутренних процессов EsportCRM',
      ],
      en: [
        'Added the EsportCRM billing account — balance and operation history',
        "The account's region now sets the billing currency: rubles or dollars",
        'The exchange rate for cross-currency operations is taken from an official Central Bank source',
        'Added region switching that converts the remaining balance into the new currency at the rate on that date',
        'Balance corrections made by support now show up in your history with a reason',
        'Improved the accuracy and reliability of financial operations and internal processes across EsportCRM',
      ],
    },
  },
  {
    version: '0.0.3',
    date: '2026-09-03',
    changes: {
      ru: [
        'EsportCRM теперь открывается по короткому адресу esportcrm.com',
      ],
      en: [
        'EsportCRM now opens at the short address esportcrm.com',
      ],
    },
  },
  {
    version: '0.0.2',
    date: '2026-09-03',
    changes: {
      ru: [
        'Повысили надёжность хранения данных и стабильность работы EsportCRM',
      ],
      en: [
        'Improved the reliability of data storage and the stability of EsportCRM',
      ],
    },
  },
  {
    version: '0.0.1',
    date: '2026-09-03',
    changes: {
      ru: [
        'Перевели EsportCRM на защищённое соединение и собственный домен',
        'Добавили журнал изменений и отображение версии приложения',
        'Улучшили расчёт выплат с учётом бонусов и штрафов',
        'Улучшили отображение статусов выплат',
        'Добавили массовую выплату с понятным результатом по каждому начислению',
        'Улучшили работу календарных ссылок',
        'Обновили отображение тарифов на главной странице',
        'Ускорили работу поддержки — новые сообщения появляются без обновления страницы',
        'Добавили удаление обращений в поддержку',
        'Добавили копирование ссылок и реквизитов одним нажатием',
        'Добавили удобный выбор часового пояса',
        'Улучшили стабильность работы финансовых разделов, списков и отчётов',
      ],
      en: [
        'Moved EsportCRM to a secure connection and its own domain',
        'Added a changelog and an app version label',
        'Improved payout calculation with bonuses and penalties taken into account',
        'Improved how payout statuses are shown',
        'Added bulk payment with a clear result for every payout',
        'Improved how calendar links work',
        'Updated how plans are shown on the landing page',
        'Made support faster — new messages appear without reloading the page',
        'Added deletion of support tickets',
        'Added one-click copying for links and payment details',
        'Added a convenient timezone picker',
        'Improved the stability of the finance sections, lists and reports',
      ],
    },
  },
];
